import React, { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Citadele } from '@/components/Icons/Citadele';
import { Luminor } from '@/components/Icons/Luminor';
import { Seb } from '@/components/Icons/Seb';
import { Swedbank } from '@/components/Icons/Swedbank';
import { Snackbar } from '@/components/Snackbar';
import { useStartBankPayment } from '@/pages/Subscription/hooks/useStartBankPayment';
import {
  BorderRadius,
  ButtonVariant,
  PaymentMethod,
  Size,
  SnackbarVariant,
} from '@/types/components';
import type { PaymentOption } from '@/types/components';
import { getRecurringEndDates } from '@/utils/subscription';

interface Props {
  option: PaymentOption;
}

export const BankMethods: React.FC<Props> = ({ option }) => {
  const { t } = useTranslation();
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);

  const methodContent: Record<PaymentMethod, ReactNode> = {
    [PaymentMethod.Seb]: <Seb className="h-7 w-auto" />,
    [PaymentMethod.Swed]: <Swedbank className="h-7 w-auto" />,
    [PaymentMethod.Luminor]: <Luminor className="h-7 w-auto" />,
    [PaymentMethod.Citadele]: <Citadele className="h-7 w-auto" />,
    [PaymentMethod.Card]: t('subscription.checkout.creditDebitCard'),
  };

  const supportedMethods = option.paymentMethods.filter(
    (method) => methodContent[method],
  );

  const { startBankPayment, pendingMethod } = useStartBankPayment({
    onError: () => setIsErrorSnackbarOpen(true),
  });

  const pay = (paymentMethod: PaymentMethod) => {
    startBankPayment({
      paymentMethod,
      paymentOptionType: option.type,
      recurringPaymentEndDate: getRecurringEndDates(option).defaultDate,
    });
  };

  return (
    <div className="flex h-full flex-col gap-3 rounded-2xl bg-card p-4 shadow-card md:p-6">
      <p className="text-[18px]/[24px] font-semibold text-foreground md:text-[20px]/[28px] md:font-medium">
        {t('subscription.checkout.bankTitle')}
      </p>
      <div className="flex flex-col gap-2">
        {supportedMethods.map((method) => (
          <Button
            key={method}
            variant={ButtonVariant.Secondary}
            size={Size.Large}
            radius={BorderRadius.Pill}
            disabled={Boolean(pendingMethod)}
            isLoading={pendingMethod === method}
            className="w-full md:rounded-xl"
            onClick={() => pay(method)}
          >
            {methodContent[method]}
          </Button>
        ))}
      </div>

      <Snackbar
        compact
        isOpen={isErrorSnackbarOpen}
        message={t('global.errorOccurred')}
        variant={SnackbarVariant.Error}
        onDismiss={() => setIsErrorSnackbarOpen(false)}
      />
    </div>
  );
};
